import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Col, Form, Input, Row, Space, Spin } from 'antd';
import useTranslation from '@hooks/useTranslation';
import { GetOrganizationDetailsResponse } from '@kemu-io/kemu-types';
import styles from './OrganizationDetails.module.css';
import StyledButton from '@components/form-control/styledButton/styledButton';
import { LoadingOutlined } from '@ant-design/icons';
import { useAccountMenuContext } from '../AccountMenuContext';

/**
 * Displays and edits the current organization's details.
 * Fetches organization info on mount, shows a spinner while loading,
 * and provides Save and Undo actions.
 * @returns React.ReactElement
 */
const OrganizationDetails: React.FC = () => {
  const { 
    organizationDetails, 
    loadingOrganizationDetails, 
    handleSaveOrgDetails,
    setOrganizationDetailsFormValues, 
    getOrganizationDetailsFormValues
  } = useAccountMenuContext();

  const [form] = Form.useForm<GetOrganizationDetailsResponse>();
  const t = useTranslation('AccountMenu.Modal.OrganizationDetails');
  const [saving, setSaving] = useState<boolean>(false);
  const [formValuesChanged, setFormValuesChanged] = useState<boolean>(false);


  const handleFormChange = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    setOrganizationDetailsFormValues(currentValues);
    const changed = JSON.stringify(currentValues) !== JSON.stringify(organizationDetails);
    setFormValuesChanged(changed);
  }, [form, setOrganizationDetailsFormValues, organizationDetails]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      await handleSaveOrgDetails(values);
    } catch(e){
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [form, handleSaveOrgDetails]);

  /** Restores the form values back to the originally loaded organization details */
  const handleUndo = (): void => {
    if (organizationDetails) {
      form.setFieldsValue(organizationDetails);
      setFormValuesChanged(false);
    }
  };

  // Set the form data on mount.
  useEffect(() => {
    const originalValues = getOrganizationDetailsFormValues();
    if (originalValues) {
      form.setFieldsValue(originalValues || {} as GetOrganizationDetailsResponse);
    }
  }, []);


  return (
    <Spin spinning={loadingOrganizationDetails} indicator={<LoadingOutlined spin style={{ fontSize: 48 }} />}>
      <div className={styles.Wrapper}>
        <div className={styles.SectionTitle}>{t('Title')}</div>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onValuesChange={handleFormChange}
        >
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                name={[ 'name' ]}
                label={t('Name')}
                rules={[{ required: true, message: t('NameRequired') }]}
              >
                <Input placeholder={t('NamePlaceholder')} data-prop-name="name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name={[ 'location', 'country' ]} label={t('Country')}>
                <Input placeholder={t('CountryPlaceholder')} data-prop-name="location.country" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={[ 'location', 'city' ]} label={t('City')}>
                <Input placeholder={t('CityPlaceholder')} data-prop-name="location.city" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name={[ 'location', 'address1' ]} label={t('Address1')}>
                <Input placeholder={t('Address1Placeholder')} data-prop-name="location.address1" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name={[ 'location', 'address2' ]} label={t('Address2')}>
                <Input placeholder={t('Address2Placeholder')} data-prop-name="location.address2" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name={[ 'location', 'postCode' ]} label={t('PostCode')}>
                <Input placeholder={t('PostCodePlaceholder')} data-prop-name="location.postCode" />
              </Form.Item>
            </Col>
          </Row>

          <Space style={{ marginTop: 16 }}>
            <StyledButton noShadow onClick={handleSave} loading={saving} disabled={!formValuesChanged} title={t.withBaseKey('CommonWords')('Save')} />
            <StyledButton noShadow color="light" onClick={handleUndo} disabled={!formValuesChanged} title={t.withBaseKey('Menu')('History.Undo')} />
          </Space>
        </Form>
      </div>
    </Spin>
  );
};

export default OrganizationDetails;